import React from 'react';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import { Circle, Mail, MessageSquare, MessageCircle, ArrowRight, MoreVertical, ArchiveIcon, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useFlags } from '@flagsmith/flagsmith/react';

import type { IConversation } from 'src/api/landlord-messages-api';
import type { IConversationMatch } from 'src/services/api/messages/use-conversations-search';
import { type IProperty, EMessageChannel, type ILead } from 'src/services/api';
import { LeadUtils } from 'src/utils';
import { Flex, FlexColumn } from 'src/components/flex';
import TableCell from 'src/components/table/TableCell';
import { StageChip } from 'src/components/input/select-lead-stage/SelectLeadStage';
import { isConversation, type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context';
import { useLead } from 'src/services/api/leads/use-lead';
import { useProperty } from 'src/services/api/properties/use-property';
import { theme } from 'src/theme/theme';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import { useOpenConversation } from 'src/pages/landlord/conversations/context/hooks/use-open-conversation';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { formatConversationDateTime } from 'src/utils/format-date-time';
import PresenceDot from 'src/components/PresenceDot';
import { EFeatureFlag, FORCE_TEAM_AVAILABILITY } from 'src/utils/feature-flags';

import styles from 'src/pages/landlord/conversations/components/list/ConversationListItem.module.css';
import { type IUser, isUserAway, EPresence } from 'src/api/users-api';

function ChannelIcon({ channel }: { channel: EMessageChannel }) {
    switch (channel) {
        case EMessageChannel.EMAIL:
            return <Mail size={20} className={styles.channelIcon} />;
        case EMessageChannel.SMS:
            return <MessageSquare size={20} className={styles.channelIcon} />;
        case EMessageChannel.FACEBOOK_MESSENGER:
            return <MessageCircle size={20} className={styles.channelIcon} />;
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _assertNever: never = channel;
        }
    }
}

function DirectionBadge({ direction }: { direction?: 'in' | 'out' }) {
    if (!direction) {
        return null;
    }
    const isInbound = direction === 'in';
    return (
        <span className={clsx(styles.directionBadge, isInbound ? styles.directionInbound : styles.directionOutbound)}>
            {isInbound ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
        </span>
    );
}

type DesktopConversationListItemProps = {
    conversation: IConversation | IConversationMatchWithChannel;
    property?: IProperty;
    lead?: ILead;
    loggedInUser: IUser;
    navigationConversations: (IConversation | IConversationMatchWithChannel)[];
    leadsByUuid: Map<string, ILead>;
    focusedConversation?: IConversation | IConversationMatchWithChannel;
    setFocusedConversation?: (conversation: IConversation | undefined) => void;
    selectedConversations?: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations?: (conversations: Array<IConversation | IConversationMatchWithChannel>) => void;
    variant?: 'default' | 'longPreview';
}

export function DesktopConversationListItem({
    conversation,
    property,
    lead,
    loggedInUser,
    navigationConversations,
    leadsByUuid,
    focusedConversation,
    setFocusedConversation,
    selectedConversations = [],
    setSelectedConversations = () => { },
    variant = 'default',
}: DesktopConversationListItemProps) {
    const openConversation = useOpenConversation(navigationConversations, leadsByUuid);
    const { userSettings } = useAuthenticatedState();
    const { data: leadData, isLoading } = useLead(conversation.lead_uuid);
    const { data: teamMembers } = useTeamMembers();
    const flags = useFlags([EFeatureFlag.TEAM_AVAILABILITY]);
    const showPresence = Boolean(flags.team_availability?.enabled) || FORCE_TEAM_AVAILABILITY;

    if (!isLoading && leadData) {
        lead = leadData;
    }

    const propertyId = lead?.property_id ?? (isConversation(conversation) ? conversation.property_id : null);
    const { data: cachedProperty } = useProperty(propertyId);
    const resolvedProperty = property ?? cachedProperty;

    let channel = isConversation(conversation) ?
        conversation.latest_message_channel :
        conversation.msg_channel;

    if (!lead) {
        console.error('Lead not found for conversation, and failed to get lead data via network request', conversation);
        return null;
    }

    if (lead.ingestion_method === 'listing-site-email' && lead.phone_number) {
        channel = EMessageChannel.SMS;
    }

    const selected = selectedConversations.some((c) => c.lead_uuid === conversation.lead_uuid);
    const isFocused = focusedConversation?.lead_uuid === conversation.lead_uuid;

    const selectConversation = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (selected) {
            setSelectedConversations(selectedConversations.
                filter((c) => c.lead_uuid !== conversation.lead_uuid)
            );
        } else {
            setSelectedConversations([...selectedConversations, conversation]);
        }
    };

    let leadOwnerName = '';
    let owner = null;
    if (teamMembers) {
        if (lead?.owner === loggedInUser.id) {
            owner = loggedInUser;
            leadOwnerName = 'You';
        } else {
            owner = teamMembers.find((o) => o.id === lead?.owner);
            leadOwnerName = owner?.name || '';
        }
    }

    const unreadCount = isConversation(conversation) ? conversation.num_unread_messages : 0;
    const hasUnreadMessages = unreadCount > 0;
    const totalTeamMembers = teamMembers?.length || 1;
    const hasOtherTeamMembers = totalTeamMembers > 1;

    const firstContact = isConversation(conversation) ? formatConversationDateTime(conversation.first_contact_at, userSettings.timezone_override) : formatConversationDateTime(lead?.inserted_at, userSettings.timezone_override);
    const lastContact = isConversation(conversation) && conversation.last_contact_at ? formatConversationDateTime(conversation.last_contact_at, userSettings.timezone_override) : undefined;

    return (
        <>
            <TableRow
                data-testid="conversation-row"
                onMouseEnter={() => setFocusedConversation?.(conversation as IConversation)}
                onClick={() => openConversation(conversation, window.location.pathname + window.location.search)}
                className={clsx(styles.tableRow, hasUnreadMessages && styles.hasUnreadMessages, isFocused && styles.focused, variant === 'longPreview' && styles.longPreview)}
            >
                <TableCell
                    width={40}
                    className={styles.tableCellWithCheckbox}
                    data-testid="checkbox-column"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Checkbox
                        className={styles.checkbox}
                        checked={selected}
                        onClick={selectConversation}
                    />
                </TableCell>

                <TableCell width={variant === 'longPreview' ? undefined : '40%'} className={clsx(styles.textWithEllipsis, styles.leadNameTableCell)} data-testid="lead-name-column">
                    <Flex alignItems="center" columnGap={1}>
                        <Typography>{LeadUtils.getLeadName(lead)}</Typography>
                    </Flex>
                </TableCell>

                <TableCell width={variant === 'longPreview' ? undefined : '20%'} className={clsx(styles.textWithEllipsis, styles.stageTableCell)} data-testid="stage-column">
                    {lead?.stage && <StageChip value={lead.stage} clickable={false} />}
                </TableCell>

                <TableCell width="50%" className={styles.textWithEllipsis} data-testid="message-preview-column">
                    <Flex alignItems="center" columnGap={1}>
                        <DirectionBadge direction={isConversation(conversation) ? conversation.last_message_direction : undefined} />
                        <ChannelIcon channel={channel} />
                        <Typography>
                            {isConversation(conversation) ?
                                conversation.latest_message_contents :
                                (conversation as IConversationMatch).matching_message_contents
                            }
                        </Typography>
                    </Flex>
                </TableCell>

                {variant !== 'longPreview' && (
                    <TableCell width="20%" className={styles.textWithEllipsis} data-testid="property-name-column">
                        <Typography>
                            {resolvedProperty?.name || 'Property not set'}
                        </Typography>
                    </TableCell>
                )}

                {hasOtherTeamMembers && (
                    <TableCell width="20%" data-testid="lead-owner-column">
                        <Flex alignItems="center" columnGap={1}>
                            <span style={{ position: 'relative', lineHeight: 0, flexShrink: 0 }}>
                                <ProfileImageRounded url={owner?.profile_photo_url} width={20} height={20} showEmptyImage userName={owner?.name} />
                                {showPresence && owner && (
                                    <span style={{ position: 'absolute', right: -1, bottom: -1 }}>
                                        <PresenceDot presence={isUserAway(owner) ? EPresence.AWAY : EPresence.ONLINE} size={7} withBorder />
                                    </span>
                                )}
                            </span>
                            <Typography className={styles.textWithEllipsis}>{leadOwnerName}</Typography>
                        </Flex>
                    </TableCell>
                )}

                <TableCell width={18} className={styles.archiveCell}>
                    {
                        lead.is_archived && <ArchiveIcon color={theme.palette.secondary.main} size={18} />
                    }
                </TableCell>

                <TableCell width={200} align="right" className={styles.textWithEllipsis} data-testid="date-column">
                    <Flex justifyContent="flex-end" alignItems="center" columnGap={1}>
                        <Typography variant="body2">{firstContact}</Typography>
                        {lastContact && firstContact !== lastContact && (
                            <>
                                <ArrowRight size={16} className={styles.channelIcon} />
                                <Typography variant="body2">{lastContact}</Typography>
                            </>
                        )}
                        {unreadCount > 0 && <span className={styles.unreadBadge}>{unreadCount}</span>}
                    </Flex>
                </TableCell>

                <TableCell width={40} data-testid="unread-indicator-column">
                    {hasUnreadMessages && <Circle size={8} className={styles.unreadIndicator} />}
                </TableCell>
            </TableRow>
        </>
    );
}

type MobileConversationListItemProps = {
    conversation: IConversation | IConversationMatchWithChannel;
    property?: IProperty;
    navigationConversations: (IConversation | IConversationMatchWithChannel)[];
    leadsByUuid: Map<string, ILead>;
    setMobileMenuAnchorEl: (anchorEl: HTMLElement) => void;
    setSelectedConversations: (conversations: Array<IConversation | IConversationMatchWithChannel>) => void;
}

export function MobileConversationListItem({
    conversation,
    property,
    navigationConversations,
    leadsByUuid,
    setMobileMenuAnchorEl,
    setSelectedConversations,
}: MobileConversationListItemProps) {
    const openConversation = useOpenConversation(navigationConversations, leadsByUuid);
    const { userSettings } = useAuthenticatedState();

    const channel = 'latest_message_channel' in conversation ? conversation.latest_message_channel : conversation.msg_channel;
    // read the lead from the RQ cache
    // this ensures we have the latest data after any updates
    const { data: lead } = useLead(conversation.lead_uuid);

    const unreadCount = 'num_unread_messages' in conversation ? conversation.num_unread_messages : 0;
    const hasUnreadMessages = unreadCount > 0;

    const firstContact = 'first_contact_at' in conversation ? formatConversationDateTime(conversation?.first_contact_at, userSettings.timezone_override) : formatConversationDateTime(conversation.matching_message_inserted_at, userSettings.timezone_override);
    const lastContact = 'last_contact_at' in conversation ? formatConversationDateTime(conversation?.last_contact_at, userSettings.timezone_override) : null;

    const latestMessageContents = 'latest_message_contents' in conversation ? conversation.latest_message_contents : conversation.matching_message_contents;

    return (
        <>
            <FlexColumn
                className={clsx(styles.mobileListItem, hasUnreadMessages && styles.hasUnreadMessages)}
                onClick={() => openConversation(conversation, window.location.pathname + window.location.search)}
            >
                <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center" width={300} columnGap={0.5}>
                        <DirectionBadge direction={isConversation(conversation) ? conversation.last_message_direction : undefined} />
                        <ChannelIcon channel={channel} />
                        {lead?.stage && <StageChip value={lead.stage} clickable={false} />}
                        <Typography className="ellipsisText">{LeadUtils.getLeadName(lead)}</Typography>
                        {hasUnreadMessages && <Circle size={8} className={styles.unreadIndicator} />}
                        {lead?.is_archived && <ArchiveIcon color={theme.palette.secondary.main} size={18} />}
                    </Flex>

                    <Flex alignItems="center" columnGap={1}>
                        {unreadCount > 0 && <span className={styles.unreadBadge}>{unreadCount}</span>}

                        <IconButton
                            onClick={(event) => {
                                event.stopPropagation();
                                setSelectedConversations([conversation]);
                                setMobileMenuAnchorEl(event.currentTarget);
                            }}
                        >
                            <MoreVertical size={20} />
                        </IconButton>
                    </Flex>
                </Flex>


                <Flex alignItems="center" columnGap={1}>
                    <Typography variant="body2">{firstContact}</Typography>
                    {firstContact !== lastContact && (
                        <>
                            <ArrowRight size={16} className={styles.channelIcon} />
                            <Typography variant="body2">{lastContact}</Typography>
                        </>
                    )}
                </Flex>

                <Typography className={styles.textWithEllipsis}>
                    {latestMessageContents}
                </Typography>

                <Typography className={styles.textWithEllipsis}>
                    {property?.name || 'Property not set'}
                </Typography>
            </FlexColumn>
        </>
    );
}
